import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DEFAULT_PAGE_LIMIT, DEFAULT_START_FROM } from "../constants";
import styles from "../../../assets/styles/pageStyles/ruleBasedInbox/threadList/threadList.module.scss";
import { useShallow } from "zustand/react/shallow";
import { useRuleBasedInboxStore } from "../../../store/ruleBasedInboxStore";

/**
 * Calculate pagination info for display
 */
const getPaginationInfo = (
    stats: { total_records: number; start_from: number; limit: number }
): {
    currentPage: number;
    totalPages: number;
    showingFrom: number;
    showingTo: number;
} => {
    const currentPage = Math.ceil(stats.start_from / stats.limit);
    const totalPages = Math.ceil(stats.total_records / stats.limit);
    const showingFrom = stats.start_from;
    const showingTo = Math.min(
        stats.start_from + stats.limit - 1,
        stats.total_records
    );

    return {
        currentPage: currentPage || 1,
        totalPages: totalPages || 1,
        showingFrom,
        showingTo,
    };
}; 

const updatePageNoParam = (pageNo: number) => {
    return (prevParams: URLSearchParams): URLSearchParams => {
        const newParams = new URLSearchParams(prevParams);
        if (pageNo <= 1) {
            newParams.delete("page");
        } else {
            newParams.set("page", String(pageNo));
        }
        return newParams;
    };
};

/**
 * Thread List Pagination Component
 * Displays pagination controls for the thread list
 */
const ThreadListPagination = () => {
    
    const { fetchThreads, stats } = useRuleBasedInboxStore(useShallow((state) => ({
        fetchThreads: state.fetchThreads,
        stats: state.stats
    })));

    const { currentPage, totalPages, showingFrom, showingTo } = getPaginationInfo(stats);

    const [searchParams, setSearchParams] = useSearchParams();
    const page = searchParams.get("page");

    const urlPageNo = Number(page) || currentPage; 

    // Handle pagination change
    const handlePageChange = useCallback(
        (startFrom: number) => {
            fetchThreads(startFrom, DEFAULT_PAGE_LIMIT);
        },
        [fetchThreads]
    );

    useEffect(() => {
        if (urlPageNo <= totalPages && currentPage <= totalPages) {
            return;
        }

        if (currentPage > totalPages) {
            handlePageChange(DEFAULT_START_FROM);
            setSearchParams(updatePageNoParam(1), { replace: true });
        }
    }, [urlPageNo, currentPage, totalPages, setSearchParams, handlePageChange]);

    const goToPage = useCallback(
        (page: number) => {
            setSearchParams(updatePageNoParam(page), { replace: true });
            handlePageChange((page - 1) * stats.limit + 1);
        },
        [setSearchParams, handlePageChange, stats.limit]
    );

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            goToPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            goToPage(currentPage + 1);
        }
    };

    // Don't render if there's no data
    if (stats.total_records === 0) {
        return null;
    }

    return (
        <div className={styles.paginationContainer}>
            <div className={styles.paginationInfo}>
                {/* Showing X-Y of Z */}
                <span className={styles.paginationText}>
                    Showing {showingFrom}-{showingTo} of {stats.total_records}
                </span>

                {/* Page Controls */}
                <div className={styles.paginationControls}>
                    {/* Previous Button */}
                    <button
                        className={styles.paginationButton}
                        onClick={handlePreviousPage}
                        disabled={currentPage <= 1}
                        aria-label="Previous page"
                    >
                        <ChevronLeftIcon sx={{ fontSize: 18 }} />
                    </button>

                    {/* Page Info */}
                    <span className={styles.pageInfo}>
                        Page {currentPage} of {totalPages}
                    </span>

                    {/* Next Button */}
                    <button
                        className={styles.paginationButton}
                        onClick={handleNextPage}
                        disabled={currentPage >= totalPages}
                        aria-label="Next page"
                    >
                        <ChevronRightIcon sx={{ fontSize: 18 }} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ThreadListPagination;
